/**
 * The places this player may see, and their waypoints.
 *
 * Over HTTP rather than the socket because the answer is **per caller**:
 * audience and fog both apply, so two players at one table get different
 * lists. A room-wide broadcast has no way to express that, which is why the
 * socket carries only the room-wide half of the map.
 *
 * Refetched whenever fog moves, because a reveal is exactly the event that
 * changes the answer — and whenever the party moves, because **the place the
 * party is standing on is exempt from fog**. Travel to somewhere never revealed
 * makes that waypoint legitimately visible for the first time, and without the
 * refetch the pin would point at a place this client does not have.
 */

import { useCallback, useEffect, useState } from 'react';

import type { Waypoint } from '@portal/shared';

import { api } from '../api.js';

export interface Place {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly waypoint?: { readonly mapX: number; readonly mapY: number; readonly regionId: string };
  /**
   * A dungeon room's doors, to other rooms' ids — absent for an ordinary
   * place. There is no printed room/corridor system to render a map from,
   * so this is text: a name and a list of exits, the same weight as any
   * other summary in this app, not a floorplan.
   */
  readonly exits?: readonly { readonly label: string; readonly toRoomId: string }[];
  /** How dangerous this room's dungeon is meant to be — absent outside a generated dungeon. */
  readonly dungeonLevel?: number;
}

/** A river or a road out of the imported world, in the same 0–100 space as a pin. */
export interface MapLine {
  readonly kind: 'river' | 'stream' | 'road' | 'trail';
  readonly name?: string;
  readonly points: readonly { readonly mapX: number; readonly mapY: number }[];
}

export function usePlaces(
  campaignId: string | undefined,
  revealed: readonly string[],
  partyLocation?: string | null,
): { places: Place[]; lines: MapLine[]; reload: () => void } {
  const [places, setPlaces] = useState<Place[]>([]);
  // **Room-wide, unlike the places beside them.** Rivers and roads are the
  // country the party is walking through, not canon somebody may be kept from —
  // the same reasoning that serves `regions` to everyone. They ride on this
  // request because it is the one the client already re-makes when the map
  // changes, not because they are per-viewer.
  const [lines, setLines] = useState<MapLine[]>([]);
  // Bumped by `reload`, so adding a place shows its pin without waiting for a
  // socket round trip. A counter rather than a flag: two reloads in a row are
  // two effects.
  const [generation, setGeneration] = useState(0);

  // Keyed on the fog list rather than its identity: the store replaces the
  // array on every `fog:update`, including ones that changed nothing, and a
  // refetch per redundant event is a request per Owner click for no new answer.
  const fogKey = [...revealed].sort().join('\0');

  useEffect(() => {
    if (!campaignId) {
      setPlaces([]);
      setLines([]);
      return;
    }
    let live = true;
    void api
      .get<{ places: Place[]; waterways?: MapLine[]; routes?: MapLine[] }>(
        `/api/campaigns/${campaignId}/places`,
      )
      .then((body) => {
        // A response that lands after the component moved on would write a
        // previous campaign's places into the current map.
        if (!live) return;
        setPlaces(body.places ?? []);
        // Waterways first, so a road drawn over a ford reads as a bridge
        // rather than the river reading as a break in the road.
        setLines([...(body.waterways ?? []), ...(body.routes ?? [])]);
      })
      // A map that will not load must not take the table down with it.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [campaignId, fogKey, generation, partyLocation]);

  const reload = useCallback(() => setGeneration((n) => n + 1), []);
  return { places, lines, reload };
}

/** The subset with a pin. Places without coordinates are canon, not geography. */
export function waypointsOf(places: readonly Place[]): Waypoint[] {
  return places.flatMap((place) =>
    place.waypoint
      ? [
          {
            placeId: place.id as Waypoint['placeId'],
            name: place.name,
            mapX: place.waypoint.mapX,
            mapY: place.waypoint.mapY,
            regionId: place.waypoint.regionId as Waypoint['regionId'],
          },
        ]
      : [],
  );
}
